/**
 * TasksCard — follow-up tasks for one contact, on the detail right rail.
 *
 * Lists open tasks due-soonest first (GET /tasks?contact=), completes them
 * with one tap (PATCH /tasks/:id { status:'done' }, optimistic with revert),
 * and creates new ones via an inline form (POST /contacts/:id/tasks) with
 * quick due-date picks for the "call back tomorrow 3pm" flow. Completed/
 * cancelled tasks live behind a collapsed toggle and can be reopened.
 */
import { FormEvent, useCallback, useEffect, useState } from 'react';
import { Check, Flag, ListTodo, Loader2, Plus, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import { Task } from '../types';
import { TASK_PRIORITIES, dueBucket, dueChipCls, dueLabel, priorityBadge, relTime, useTeam } from '../lib';
import { Alert } from './ui';

const errMsg = (e: any): string =>
  e?.response?.data?.detail || e?.response?.data?.error || e?.message || 'Request failed';

const isOpen = (t: Task) => t.status === 'open' || t.status === 'in_progress';

/** A Date as a datetime-local input value (local clock, minute precision). */
function toLocalInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Local datetime `days` ahead at `hour`:00, as a datetime-local value. */
function quickDue(days: number, hour: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  d.setHours(hour, 0, 0, 0);
  return toLocalInput(d);
}

const QUICK_DUES: Array<{ label: string; value: () => string }> = [
  { label: 'Tomorrow 9am', value: () => quickDue(1, 9) },
  { label: 'Tomorrow 3pm', value: () => quickDue(1, 15) },
  { label: 'In 3 days', value: () => quickDue(3, 9) },
];

interface Props {
  contactId: string;
  /** Rendered inside a rail disclosure row, which owns the chrome: drop this
   *  card's own border/background/shadow. Absent = the standalone card, byte
   *  for byte what the lead page renders today. */
  frameless?: boolean;
}

export default function TasksCard({ contactId, frameless = false }: Props) {
  const { user } = useAuth();
  const { team } = useTeam();

  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busyIds, setBusyIds] = useState<Set<string>>(new Set());
  const [showClosed, setShowClosed] = useState(false);

  // Inline create form
  const [formOpen, setFormOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState('');
  const [due, setDue] = useState('');
  const [priority, setPriority] = useState('normal');
  const [assignee, setAssignee] = useState(user?.id || '');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/api/crm/tasks', { params: { contact: contactId, limit: 100 } });
      setTasks(res.data?.tasks || []);
    } catch (e: any) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [contactId]);

  useEffect(() => { load(); }, [load]);

  // Default the assignee once auth resolves (user can be null on first render).
  useEffect(() => { setAssignee((a) => a || user?.id || ''); }, [user?.id]);

  const setBusy = (id: string, busy: boolean) =>
    setBusyIds((prev) => {
      const n = new Set(prev);
      if (busy) n.add(id); else n.delete(id);
      return n;
    });

  /** Optimistically flip a task's status; revert on failure. */
  const setStatus = async (task: Task, status: string) => {
    if (busyIds.has(task.id)) return;
    setActionError(null);
    setBusy(task.id, true);
    setTasks((prev) => prev.map((t) => (t.id === task.id ? { ...t, status } : t)));
    try {
      const res = await api.patch(`/api/crm/tasks/${task.id}`, { status });
      const updated = res.data?.task;
      if (updated) setTasks((prev) => prev.map((t) => (t.id === task.id ? { ...t, ...updated } : t)));
    } catch (e: any) {
      setTasks((prev) => prev.map((t) => (t.id === task.id ? { ...t, status: task.status } : t)));
      setActionError(errMsg(e));
    } finally {
      setBusy(task.id, false);
    }
  };

  const resetForm = () => {
    setTitle('');
    setDue('');
    setPriority('normal');
    setAssignee(user?.id || '');
  };

  const create = async (e: FormEvent) => {
    e.preventDefault();
    if (!title.trim() || saving) return;
    setSaving(true);
    setActionError(null);
    try {
      const res = await api.post(`/api/crm/contacts/${contactId}/tasks`, {
        title: title.trim(),
        due_at: due ? new Date(due).toISOString() : null,
        priority,
        assignee_user_id: assignee || null,
      });
      const task: Task | undefined = res.data?.task;
      if (task) {
        setTasks((prev) =>
          [...prev, task].sort((a, b) =>
            (a.due_at || '9999').localeCompare(b.due_at || '9999') || b.created_at.localeCompare(a.created_at)
          )
        );
      }
      resetForm();
      setFormOpen(false);
    } catch (e2: any) {
      setActionError(errMsg(e2));
    } finally {
      setSaving(false);
    }
  };

  const open = tasks.filter(isOpen);
  const closed = tasks.filter((t) => !isOpen(t));

  const inputCls =
    'w-full px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm text-gray-900 placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors duration-150 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-400';

  const renderRow = (t: Task) => {
    const done = !isOpen(t);
    const busy = busyIds.has(t.id);
    const bucket = dueBucket(t.due_at);
    const prioCls = priorityBadge(t.priority);
    return (
      <div key={t.id} className="flex items-start gap-2.5 py-2.5">
        <button
          onClick={() => setStatus(t, done ? 'open' : 'done')}
          disabled={busy}
          title={done ? 'Reopen task' : 'Mark done'}
          aria-label={done ? `Reopen task: ${t.title}` : `Mark task done: ${t.title}`}
          className={`mt-0.5 flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full border transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-wait disabled:opacity-50 sm:h-6 sm:w-6 ${
            done
              ? 'border-green-600 bg-green-600 text-white hover:bg-green-700'
              : 'border-gray-300 text-transparent hover:border-primary-600 hover:text-primary-600 dark:border-gray-600'
          }`}
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-400" /> : <Check className="h-3.5 w-3.5" />}
        </button>
        <div className="min-w-0 flex-1">
          <p
            className={`break-words text-sm ${
              done ? 'text-gray-400 line-through dark:text-gray-500' : 'font-medium text-gray-900 dark:text-gray-100'
            }`}
          >
            {t.title}
          </p>
          {t.description && !done && (
            <p className="mt-0.5 break-words text-xs text-gray-500 dark:text-gray-400">{t.description}</p>
          )}
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            {t.due_at && !done && (
              <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${dueChipCls(bucket)}`}>
                {dueLabel(t.due_at)}
              </span>
            )}
            {prioCls && !done && (
              <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${prioCls}`}>
                <Flag className="h-3 w-3" /> {t.priority}
              </span>
            )}
            {done && <span className="text-xs text-gray-400">Closed {relTime(t.updated_at || t.created_at)}</span>}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      className={frameless
        ? ''
        : 'rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:p-6'}
    >
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="inline-flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-gray-100">
          <ListTodo className="h-4 w-4 text-primary-600 dark:text-primary-400" />
          Tasks
          {!loading && open.length > 0 && (
            <span className="text-sm font-normal text-gray-500 dark:text-gray-400">({open.length})</span>
          )}
        </h3>
        <button
          onClick={() => { setFormOpen((s) => !s); setActionError(null); }}
          aria-expanded={formOpen}
          className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-primary-600 px-3 py-1.5 text-xs font-medium text-primary-700 transition-colors duration-150 hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-primary-400 dark:hover:bg-primary-900/20 sm:min-h-0"
        >
          {formOpen ? <X className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
          {formOpen ? 'Cancel' : 'Add task'}
        </button>
      </div>

      {formOpen && (
        <form onSubmit={create} className="mb-3 space-y-2.5 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-900/40">
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Call back about screening visit…"
            aria-label="Task title"
            maxLength={200}
            autoFocus
            className={inputCls}
          />
          <div>
            <input
              type="datetime-local"
              value={due}
              onChange={(e) => setDue(e.target.value)}
              aria-label="Due date and time"
              className={inputCls}
            />
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {QUICK_DUES.map((q) => (
                <button
                  key={q.label}
                  type="button"
                  onClick={() => setDue(q.value())}
                  className="rounded-full border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-600 transition-colors duration-150 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  {q.label}
                </button>
              ))}
              {due && (
                <button
                  type="button"
                  onClick={() => setDue('')}
                  className="rounded-full px-2.5 py-1 text-xs font-medium text-gray-400 transition-colors duration-150 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:hover:text-gray-300"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <select
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
              aria-label="Priority"
              className={`${inputCls} cursor-pointer`}
            >
              {TASK_PRIORITIES.map((p) => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
            <select
              value={assignee}
              onChange={(e) => setAssignee(e.target.value)}
              aria-label="Assignee"
              className={`${inputCls} cursor-pointer`}
            >
              <option value="">Unassigned</option>
              {team.map((m) => (
                <option key={m.id} value={m.id}>{m.id === user?.id ? `${m.name} (me)` : m.name}</option>
              ))}
            </select>
          </div>
          <button
            type="submit"
            disabled={!title.trim() || saving}
            className="inline-flex min-h-[44px] w-full items-center justify-center gap-1.5 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:focus:ring-offset-gray-900 sm:min-h-0"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            {saving ? 'Adding…' : 'Add task'}
          </button>
        </form>
      )}

      {actionError && (
        <Alert tone="error" size="sm" icon={false} className="mb-3">{actionError}</Alert>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-6">
          <Loader2 className="h-4 w-4 animate-spin text-primary-600" />
        </div>
      ) : error ? (
        <Alert tone="error" icon={false}>
          {error}
          <button onClick={load} className="ml-2 font-medium underline hover:no-underline">Retry</button>
        </Alert>
      ) : (
        <>
          {open.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No open tasks. Add a follow-up so this lead doesn't slip.
            </p>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-gray-700">{open.map(renderRow)}</div>
          )}

          {closed.length > 0 && (
            <div className="mt-2 border-t border-gray-100 pt-2 dark:border-gray-700">
              <button
                onClick={() => setShowClosed((s) => !s)}
                aria-expanded={showClosed}
                className="text-xs font-medium text-gray-500 transition-colors duration-150 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-400 dark:hover:text-gray-200"
              >
                {showClosed ? 'Hide' : 'Show'} completed ({closed.length})
              </button>
              {showClosed && (
                <div className="divide-y divide-gray-100 dark:divide-gray-700">{closed.map(renderRow)}</div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
